/**
 * Stockage du jeton, et rien d'autre.
 *
 * `localStorage` plutôt qu'un cookie : l'API attend le jeton dans l'en-tête
 * `Authorization`, le front est servi depuis une autre origine que l'API, et un
 * cookie tiers ne survivrait pas aux réglages navigateur par défaut. C'est le
 * même choix que l'app cliente de prod.
 *
 * Contrepartie assumée : un XSS dans le dashboard donne accès au jeton. Le
 * risque est faible ici — pas de contenu utilisateur affiché, un bundle React
 * sans `dangerouslySetInnerHTML` — il expire au bout de 30 jours, et l'API
 * peut le révoquer avant (changement de mot de passe, « Déconnecter tous les
 * appareils »).
 *
 * Le jeton d'appareil de confiance (second facteur non redemandé pendant
 * 30 jours) vit à côté, et SURVIT à la déconnexion : c'est le navigateur qui
 * est de confiance, pas la session. Il ne vaut que pour le compte qui l'a
 * obtenu ; l'API le vérifie.
 *
 * Tous les accès passent par try/catch : `localStorage` lève en navigation
 * privée stricte et quand les données de site sont bloquées.
 */

import type { AdminRole } from '@shared/types/auth';

const TOKEN_KEY = 'pianoled.analytics.token';
const EMAIL_KEY = 'pianoled.analytics.email';
const DEVICE_KEY = 'pianoled.analytics.device';
const ROLE_KEY = 'pianoled.analytics.role';

export interface StoredSession {
  token: string;
  email: string;
  /** Absent d'une session ouverte avant les rôles : relu via /api/auth/me. */
  role: AdminRole | null;
}

export function readSession(): StoredSession | null {
  try {
    const token = window.localStorage.getItem(TOKEN_KEY);
    const email = window.localStorage.getItem(EMAIL_KEY);
    if (!token || !email) return null;
    const role = window.localStorage.getItem(ROLE_KEY);
    return { token, email, role: role === 'admin' || role === 'user' ? role : null };
  } catch {
    return null;
  }
}

export function writeSession(session: StoredSession): void {
  try {
    window.localStorage.setItem(TOKEN_KEY, session.token);
    window.localStorage.setItem(EMAIL_KEY, session.email);
    if (session.role) window.localStorage.setItem(ROLE_KEY, session.role);
    else window.localStorage.removeItem(ROLE_KEY);
  } catch {
    // Session non persistee : l'utilisateur devra se reconnecter au prochain
    // chargement. Le state React tient le temps de l'onglet, ca suffit.
  }
}

export function clearSession(): void {
  try {
    window.localStorage.removeItem(TOKEN_KEY);
    window.localStorage.removeItem(EMAIL_KEY);
    window.localStorage.removeItem(ROLE_KEY);
  } catch {
    // Rien a faire : la session en memoire est de toute facon abandonnee.
  }
}

export function readDeviceToken(): string | undefined {
  try {
    return window.localStorage.getItem(DEVICE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export function writeDeviceToken(token: string): void {
  try {
    window.localStorage.setItem(DEVICE_KEY, token);
  } catch {
    // Pas grave : le code sera simplement redemande la prochaine fois.
  }
}

export function clearDeviceToken(): void {
  try {
    window.localStorage.removeItem(DEVICE_KEY);
  } catch {
    // Idem.
  }
}
